<script lang="ts">
	// @ts-nocheck
	import slugify from 'slugify';
	import * as Carousel from '$lib/components/ui/carousel';
	import * as Card from '$lib/components/ui/card';
	import Button from '../ui/button/button.svelte';
	import { Badge } from '$lib/components/ui/badge';
	import { ArrowRight, File, Map } from 'lucide-svelte';
	import allToursData from '$lib/data/allTours.json';
	import { enhancedImages } from '$lib/utils/images';

	interface Props {
		selected?: string;
		heading?: string;
		productCode?: string;
	}

	let { selected = 'featured', heading = 'Featured tours', productCode = '' }: Props = $props();

	const productCodes = ['PTC0KJ', 'P18ZC5', 'PQ5CJC', 'PJHLE0', 'PNNK81', 'PNVP8M', 'P6GE12'];
	const dayTours = ['P18ZC5', 'PQ5CJC', 'PJHLE0', 'PNVP8M', 'PP0SGV', 'P6GE12'];
	const multiDayTours = ['P1AHF0', 'PVHGUQ', 'PTWH1T', 'PM5WCG', 'PTNCNK'];
	const shoreExcursions = ['P0J8QR', 'PM0VRV', 'PB2LG2', 'PSP9WG', 'P9TVKQ'];
	const privateTours = ['P80WBP', 'PUYDUT', 'PMFU3K', 'PVSUAB', 'P0QZS7'];

	function removeProductCodeFromLists(code) {
		const lists = [productCodes, dayTours, multiDayTours, shoreExcursions, privateTours];
		lists.forEach((list) => {
			const index = list.indexOf(code);
			if (index !== -1) {
				list.splice(index, 1);
			}
		});
	}

	if (productCode) {
		removeProductCodeFromLists(productCode);
	}

	let featuredTours = $derived(
		selected === 'featured'
			? allToursData.filter((tour) => productCodes.includes(tour.productCode))
			: selected === 'dayTours'
				? allToursData.filter((tour) => dayTours.includes(tour.productCode))
				: selected === 'multiDayTours'
					? allToursData.filter((tour) => multiDayTours.includes(tour.productCode))
					: selected === 'shoreExcursions'
						? allToursData.filter((tour) => shoreExcursions.includes(tour.productCode))
						: selected === 'privateTours'
							? allToursData.filter((tour) => privateTours.includes(tour.productCode))
							: []
	);

	// Function to check if an image URL is in the enhancedImages collection
	function isEnhancedImage(imageUrl) {
		if (!imageUrl) return false;
		return !!Object.entries(enhancedImages).find(([key]) => key === imageUrl);
	}

	// Function to get the appropriate image source
	function getImageSource(imageUrl) {
		if (!imageUrl) return null;

		// If it's an enhanced image, get it from the enhancedImages collection
		const enhancedEntry = Object.entries(enhancedImages).find(([key]) => key === imageUrl);
		if (enhancedEntry) {
			return enhancedEntry[1].default;
		}

		// Otherwise, use the URL directly
		return imageUrl;
	}

	// Function to format duration
	function formatDuration(durationMinutes) {
		const hours = Math.ceil(durationMinutes / 60); // Round up to nearest hour
		if (hours >= 24) {
			const days = Math.ceil(hours / 24); // Round up to nearest day
			return `${days} day${days > 1 ? 's' : ''}`;
		} else {
			return `${hours} hour${hours > 1 ? 's' : ''}`;
		}
	}

	function formatPrice(price) {
		return `From AUD $${price.toFixed(2)}`;
	}

	function formatProductType(productType) {
		const productTypeLabels = {
			DAYTOUR: 'Day Tour',
			MULTIDAYTOUR: 'Multi-day',
			SHOREEXCURSION: 'Shore Excursion',
			PRIVATETOUR: 'Private'
		};

		return productTypeLabels[productType] || productType; // Fallback to original value if no mapping found
	}
</script>

<section
	class="mx-auto flex max-w-7xl flex-col overflow-hidden px-4 pb-12 pt-20 lg:overflow-visible"
>
	<h2
		class="mt-10 scroll-m-20 pb-12 font-heading text-2xl font-semibold tracking-tight transition-colors first:mt-0 lg:text-3xl"
	>
		{heading}
	</h2>
	<div class="mx-auto mt-12 mt-6 flex w-full max-w-7xl">
		<Carousel.Root class="w-full" opts={{ align: 'center', loop: true }}>
			<Carousel.Content>
				{#each featuredTours as tour}
					<Carousel.Item class="w-full lg:basis-1/3">
						<a
							data-sveltekit-noscroll
							href={`/tours/${slugify(tour.name, { lower: true, strict: true, remove: `/[*+~.()'"!:@]/g` })}`}
							data-sveltekit-prefetch
							class="col-span-1"
						>
							<Card.Root class="w-full overflow-hidden rounded-2xl transition-all">
								<Card.Header class="relative w-full p-0">
									{#if tour.images && tour.images.length > 0}
										{#if isEnhancedImage(tour.images[0].itemUrl)}
											<enhanced:img
												alt={tour.name}
												class="h-48 w-full object-cover"
												src={getImageSource(tour.images[0].itemUrl)}
												sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
												loading="lazy"
											/>
										{:else}
											<img
												alt={tour.name}
												class="h-48 w-full object-cover"
												src={tour.images[0].itemUrl}
												sizes="(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw"
												loading="lazy"
											/>
										{/if}
									{:else}
										<File class="h-48 w-full object-cover text-muted-foreground" />
									{/if}
									<Badge
										class="absolute left-6 top-4 bg-[#ffd451] text-foreground hover:bg-[#ffd451]/80"
										>{formatProductType(tour.productType)}</Badge
									>
									<Card.Title class=" h-24 px-4 py-4 font-heading"
										><p class="line-clamp-3">{tour.name}</p></Card.Title
									>
									<Card.Description class="px-4 font-semibold "
										>{formatPrice(tour.advertisedPrice)}</Card.Description
									>
									<Card.Description class="px-4"
										>duration: {formatDuration(tour.durationMinutes)}</Card.Description
									>
								</Card.Header>
								<Card.Content class="h-32 px-4 py-4">
									<div>
										<p class="text-sm">
											{tour.shortDescription}
										</p>
									</div>
								</Card.Content>
								<Card.Footer class="flex justify-between px-4">
									<Button variant="link" class="pl-0 text-sm font-semibold text-foreground">
										Learn more <ArrowRight class="ml-2 h-4 w-4" />
									</Button>
									<Button
										aria-label="add to trip"
										variant="secondary"
										size="icon"
										class="rounded-full"
									>
										<Map class="h-4 w-4 stroke-primary/60" />
									</Button>
								</Card.Footer>
							</Card.Root>
						</a>
					</Carousel.Item>
				{/each}
			</Carousel.Content>
			<Carousel.Previous class="" />
			<Carousel.Next class="" />
		</Carousel.Root>
	</div>
</section>
